import React, { useMemo, useEffect, useState, useCallback, useRef } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import IsMobile from "../../hooks/isMobile";
import { ROUTE_PATHS } from "../../routes";
import { useAppSelector, useAppDispatch } from "../../hooks/storeHooks";
import ChatDetailHeader from "../../components/pages-components/chat-page-components/ChatDetailHeader/ChatDetailHeader";
import EventDetailsDrawer from "../../components/pages-components/chat-page-components/EventDetailsDrawer/EventDetailsDrawer";
import {
	respondToEventFilterThunk,
	getEventBidThunk,
	getEventDetailThunk,
	declineEventRequestThunk,
} from "../../store/events/eventThunk";
import {
	getCorporateEventDetailThunk,
	getCorporateEventBidsThunk,
	declineCorporateEventThunk,
} from "../../store/b2b/corporateEvent/corporateEventThunk";
import {
	IEventDetailMessages,
	RESPOND_TO_EVENT_TYPES,
	iBidType,
	iEventType,
	iRespondEventPayload,
} from "../../types/eventTypes";
import { ICorporateEvent, ICorporateEventMessage } from "../../interfaces/corporateEvent";
import { EventStatusEnum } from "../../types/eventTypes";
import {
	CorporateEventStatusEnum,
	ChatFilterTypeEnum,
	EventTypeFilterEnum,
	RespondToCorporateEventInviteEnum,
} from "../../constants/enums";
import {
	parseFiltersFromURL,
	buildApiParamsHelper,
	convertToAppliedFilters,
	buildChatsURLSearchParams,
	isObjectEmpty,
	getEventTypeFromEventObject,
	showDeclineEventButtonHelper,
} from "../../helpers/common";
import { getAllChatsRecord, getUnreadChannelsCount } from "../../store/chats/chatsThunk";
import ChatPageSendbirdChat from "../../components/pages-components/chat-page-components/ChatPageSendbirdChat/ChatPageSendbirdChat";
import EmptyChatComponent from "../../components/pages-components/chat-page-components/ChatDetailHeader/EmptyChatComponent/EmptyChatComponent";
import { BUTTON_COLOR, BUTTON_VARIANT } from "../../types/coreButtonTypes";
import { useEventStatusManagement } from "../../hooks/allChatsPageHooks/useEventStatusManagement";
import { useToast } from "../../context/ToasterContext";
import { REDUX_REQUEST_STATUS } from "../../types/commonTypes";
import { iType } from "../../types/eventTypes";
import { DECLINE_VENDOR_REASONS } from "../../constants/formConstants";
import DeclineEventModal from "../../components/pages-components/decline-event-modal/DeclineEventModal";
import { unwrapResult } from "@reduxjs/toolkit";
import "./chats.scss";
import ShopifyDeclineModal from "../../components/reusable-components/modals/shopify-decline-modal/ShopifyDeclineModal";
import { applyOpenChatPreview, setEventType, setSkipNextFirestoreUpdateDetail } from "../../store/chats/chatsSlice";
import { resetEventDetail } from "../../store/events/eventSlice";
import { setCorporateEventDetail } from "../../store/b2b/corporateEvent/corporateEventSlice";
import { DEFAULT_PAGE_LIMIT_MESSAGING } from "../../constants/commonConstants";
import { useOnUpdateEvent } from "../../hooks/firestoreHooks/eventUpdated";
import { useOnUpdateCorporateEvent } from "../../hooks/firestoreHooks/corporateEventHook";
import { store } from "../../store";
import { MESSAGING_ARCHIVED_VIEW } from "../../constants/chatConstants";
import { ConversationSkeleton } from "../../components/pages-components/chat-page-components/MessagingSkeleton/ConversationSkeleton";
import { getLoggedInVendorDisplayName, getSendbirdInboxSnippet } from "../../helpers/sendbirdDisplay";
import useUserInfo from "../../hooks/reduxHelpersHooks/useUserInfo";

const ChatDetailPage: React.FC = () => {
	const isMobile = IsMobile();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { eventId, chatId } = useParams<{ eventId?: string; chatId?: string }>();
	const [searchParams, setSearchParams] = useSearchParams();
	const isArchivedView = searchParams.get("view") === MESSAGING_ARCHIVED_VIEW;
	const archivedSearchTerm = searchParams.get("archivedSearch") || "";
	const { userDetails } = useUserInfo();
	const isEventDetailsFetchedRef = useRef<boolean>(false);
	const getDetailsByEventIdRef = useRef<(id: string) => Promise<void>>(() => Promise.resolve());
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const { showToast } = useToast();
	const statusManagement = useEventStatusManagement();
	const [loading, setLoading] = useState(false);
	const [shouldStartUpdatingEvent, setShouldStartUpdatingEvent] = useState(false);
	const [chatMessage, setChatMessage] = useState<IEventDetailMessages | ICorporateEventMessage | null>(null);
	const [fetchingEventRecord, setFetchingEventRecord] = useState(false);
	const { chatsRecord, isLoading, eventType } = useAppSelector((state) => state.chats);
	const isLoadingRef = useRef<boolean>(isLoading); // Use this to skip chat record API call if any other of same API call is in progress

	// Get current search and filter params from URL
	const searchTerm = searchParams.get("search") || "";
	const filterType =
		searchParams.get("isUnreadOnly") === "true" ? ChatFilterTypeEnum.Unread : ChatFilterTypeEnum.All;

	const appliedFilters = useMemo(() => parseFiltersFromURL(searchParams), [searchParams]);

	const b2cEventDetail = useAppSelector((state) => state.events.event);
	const b2bEventDetail = useAppSelector((state) => state.corporateEvent.corporateEventDetail);

	// Keep ref in sync with isLoading
	useEffect(() => {
		isLoadingRef.current = isLoading;
	}, [isLoading]);

	const eventDetail = useMemo(() => {
		if (!eventId) return null;
		if (eventType === EventTypeFilterEnum.B2B) {
			return b2bEventDetail;
		}
		return b2cEventDetail;
	}, [eventType, b2bEventDetail, b2cEventDetail]);

	const getDetailsByEventId = async (eventId: string) => {
		try {
			// Only set to true if object is empty
			const hadEventDetails = b2cEventDetail?.id === eventId || b2bEventDetail?.id === eventId;
      if (!hadEventDetails) {
        setFetchingEventRecord(true);
      }
			isEventDetailsFetchedRef.current = false;

			const loadB2c = async () => {
				const b2cResponse = await dispatch(getEventDetailThunk({ id: eventId }));
				if (b2cResponse.meta.requestStatus !== REDUX_REQUEST_STATUS.FULFILLED) return false;
				isEventDetailsFetchedRef.current = true;
				const b2cEvent = unwrapResult(b2cResponse);
				const eventType = getEventTypeFromEventObject(b2cEvent);

				dispatch(setEventType(eventType));
				setShouldStartUpdatingEvent(true);

				// Get bids only for B2C events - not prepaid
				if (eventType === EventTypeFilterEnum.B2C) {
					dispatch(getEventBidThunk({ id: eventId }));
				}
				return true;
			};

			const loadB2b = async () => {
				const b2bResponse = await dispatch(getCorporateEventDetailThunk(eventId));
				if (b2bResponse.meta.requestStatus !== REDUX_REQUEST_STATUS.FULFILLED) return false;
				isEventDetailsFetchedRef.current = true;
				dispatch(setEventType(EventTypeFilterEnum.B2B));
				setShouldStartUpdatingEvent(true);
				await dispatch(getCorporateEventBidsThunk(eventId));
				return true;
			};

			const { chatsRecord: inbox, detachedInboxEvents } = store.getState().chats;
			const inboxEventType = (inbox?.events?.find((event) => event.eventId === eventId) ?? detachedInboxEvents[eventId])
				?.eventType;
			const loaded =
				inboxEventType === EventTypeFilterEnum.B2B
					? await loadB2b()
					: inboxEventType
						? await loadB2c()
						: (await loadB2c()) || (await loadB2b());
			if (!loaded) {
				console.error("Event not found", eventId);
			}
		} catch (error) {
			console.error("Error fetching event details:", error);
		} finally {
			setFetchingEventRecord(false);
		}
	};

	getDetailsByEventIdRef.current = getDetailsByEventId;

	const fetchEventDetails = useCallback((id: string) => {
		if (id) getDetailsByEventIdRef.current(id);
	}, []);

	// Fetch event details and bids when chat is selected
	useEffect(() => {
		if (eventId) fetchEventDetails(eventId);
	}, [eventId, fetchEventDetails]);

	// Stable callback so the Firestore hooks don't re-run every render and trigger a loop
	const onEventUpdate = useCallback(() => fetchEventDetails(eventId || ''), [eventId, fetchEventDetails]);

	const handleFirestoreEventUpdate = useCallback(() => {
		const skip = store.getState().chats.skipNextFirestoreUpdateDetail;
		if (skip) {
			dispatch(setSkipNextFirestoreUpdateDetail(false));
			return;
		}
		onEventUpdate();
	}, [dispatch, onEventUpdate]);

	useOnUpdateEvent(shouldStartUpdatingEvent, handleFirestoreEventUpdate, eventId || "");
	useOnUpdateCorporateEvent(shouldStartUpdatingEvent, handleFirestoreEventUpdate, eventId || '');

	const handleBackClick = () => {
		const newSearchParams = buildChatsURLSearchParams(appliedFilters, searchTerm, filterType);
		if (isArchivedView) {
			newSearchParams.set("view", MESSAGING_ARCHIVED_VIEW);
			if (archivedSearchTerm) newSearchParams.set("archivedSearch", archivedSearchTerm);
		}
		const queryString = newSearchParams.toString();
		handleMessageUpdates();
		navigate(ROUTE_PATHS.CHATS + (queryString ? `?${queryString}` : ""));
	};

	const fetchChatsRecord = () => {
    let limit = DEFAULT_PAGE_LIMIT_MESSAGING;
    if (chatsRecord?.events) {
      limit =
        chatsRecord?.events?.length > DEFAULT_PAGE_LIMIT_MESSAGING
          ? chatsRecord?.events?.length
          : DEFAULT_PAGE_LIMIT_MESSAGING;
    }

    dispatch(getAllChatsRecord({ ...buildApiParams(limit), silent: true }));
  };

	const handleEventStatusChange = () => {
		// This will be called when event status changes
		// Could trigger a refresh of chat data if needed
		fetchChatsRecord();
		handleMessageUpdates();
	};

	const handleDrawerOpen = () => {
		setIsDrawerOpen((prev) => !prev);
	};

	const handleDrawerClose = () => {
		setIsDrawerOpen(false);
	};

	useEffect(() => {
		if (isObjectEmpty(eventDetail)) return;

		let selectedChatMessage: IEventDetailMessages | ICorporateEventMessage | null = null;
		if (eventType === EventTypeFilterEnum.B2C || eventType === EventTypeFilterEnum.Prepaid) {
			selectedChatMessage =
				b2cEventDetail?.messages?.find((message: IEventDetailMessages) => message.vendorEventId === chatId) ||
				null;
		} else if (eventType === EventTypeFilterEnum.B2B) {
			selectedChatMessage =
				b2bEventDetail?.messages?.find(
					(message: ICorporateEventMessage) => message.corporateEventVendorId === chatId,
				) || null;
		}
		setChatMessage(selectedChatMessage);
	}, [chatId, eventDetail, eventType]);

	const shouldOpenDetails = searchParams.get("details") === "open";
	useEffect(() => {
		if (!shouldOpenDetails || !chatMessage) return;
		setIsDrawerOpen(true);
		setSearchParams(
			(prev) => {
				prev.delete("details");
				return prev;
			},
			{ replace: true },
		);
	}, [shouldOpenDetails, chatMessage, setSearchParams]);

	const handleDeclineEvent = useCallback(async () => {
		if (!eventDetail) return;

		setLoading(true);
		try {
			if (eventType === EventTypeFilterEnum.B2B) {
				const res = await dispatch(
					declineCorporateEventThunk({
						corporateEventId: eventDetail.id,
						body: {
							status: RespondToCorporateEventInviteEnum.Decline,
							declineReason: {
								value: statusManagement.otherValue
									? ""
									: DECLINE_VENDOR_REASONS.find(
											(reason) => reason.FormControlLabelValue === statusManagement.radioGroupValue,
										)?.FormControlLabelLabel,
							} as iType,
							otherDeclineReason: statusManagement.otherValue,
						},
					}),
				);
				if (res.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
					dispatch(setCorporateEventDetail(null));
					statusManagement.setOtherValue("");
					statusManagement.setRadioGroupValue("");
					statusManagement.closeDeclineModal();
					showToast("Event declined successfully", () => {}, "success");
					handleEventStatusChange();
					navigate(ROUTE_PATHS.CHATS);
				} else {
					showToast("Something went wrong", () => {}, "error");
				}
			} else {
				const payload = {
					id: eventDetail.id,
					status: RESPOND_TO_EVENT_TYPES.DECLINE,
					declineReason: {
						value: statusManagement.otherValue
							? ""
							: DECLINE_VENDOR_REASONS.find(
									(reason) => reason.FormControlLabelValue === statusManagement.radioGroupValue,
								)?.FormControlLabelLabel || "",
					} as iType,
					otherDeclineReason: statusManagement.otherValue,
				} as iRespondEventPayload;

				const resp = await dispatch(respondToEventFilterThunk(payload));

				if (resp.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
					dispatch(resetEventDetail());
					statusManagement.setOtherValue("");
					statusManagement.setRadioGroupValue("");
					statusManagement.closeDeclineModal();
					showToast("Event declined successfully", () => {}, "success");
					handleEventStatusChange();
					navigate(ROUTE_PATHS.CHATS);
				}
			}
		} catch (err: any) {
			showToast(err.message || "Failed to cancel event", () => {}, "error");
		} finally {
			setLoading(false);
		}
	}, [eventDetail, statusManagement, dispatch, showToast, handleEventStatusChange, eventType]);

	const handleStatusChange = useCallback(
		async (status: EventStatusEnum | CorporateEventStatusEnum) => {
			if (status === EventStatusEnum.Cancelled || status === CorporateEventStatusEnum.Cancelled) {
				statusManagement.openDeclineModal();
			}
		},
		[statusManagement, handleEventStatusChange],
	);

	// Build API params with current filters (same as ChatsListPage)
	const buildApiParams = useCallback(
		(limit: number = DEFAULT_PAGE_LIMIT_MESSAGING) =>
			isArchivedView
				? { page: 1, limit, search: archivedSearchTerm.trim() || undefined, archived: true }
				: buildApiParamsHelper(
						searchTerm,
						filterType,
						appliedFilters,
						chatsRecord?.pagination?.currentPage ?? 1,
						limit,
					),
		[isArchivedView, archivedSearchTerm, searchTerm, filterType, appliedFilters, chatsRecord?.pagination?.currentPage],
	);

	// Handle message updates - refresh chat list
	const handleMessageUpdates = useCallback(() => {
		if (isLoadingRef.current || isArchivedView) return;
		dispatch(getUnreadChannelsCount({ ...buildApiParams(), silent: true }));
	}, [dispatch, buildApiParams, isArchivedView]);

	const handleChannelPreviewUpdate = useCallback(
		(channelUrl: string, lastMessage: unknown) => {
			const message = lastMessage as { messageId?: number; createdAt?: number } | null;
			if (!message?.messageId || !message.createdAt) return;
			const chat = chatsRecord?.events?.flatMap((event) => event.chats).find((row) => row.channelUrl === channelUrl);
			if (!chat) return;
			dispatch(
				applyOpenChatPreview({
					channelUrl,
					lastMessage:
						getSendbirdInboxSnippet(lastMessage as Parameters<typeof getSendbirdInboxSnippet>[0], {
							isNewModelChannel: chat.isNewModelChannel,
							currentUserId: userDetails?.sendbirdId,
							loggedInVendorName: getLoggedInVendorDisplayName(userDetails),
						}) || "",
					lastMessageAt: message.createdAt,
					lastMessageId: message.messageId,
				}),
			);
		},
		[chatsRecord?.events, dispatch, userDetails],
	);

	const hasFilters = useMemo(() => {
		const converToAppliedFilters = convertToAppliedFilters(appliedFilters);
		return (
			converToAppliedFilters.orderId ||
			converToAppliedFilters.eventDateStart ||
			converToAppliedFilters.createdDateStart ||
			(converToAppliedFilters.vendorEventStatus && converToAppliedFilters.vendorEventStatus.length > 0) ||
			converToAppliedFilters.eventType
		);
	}, [appliedFilters]);

	const emptyChatHeading = useMemo(() => {
		return (hasFilters || searchTerm.trim() !== "" || filterType) && chatsRecord?.events?.length === 0
			? "No messages"
			: null;
	}, [hasFilters, searchTerm, chatsRecord, filterType]);

	const showDeclineEventButtonValue = useMemo(() => {
		if (!eventDetail || !eventType) return false;
		return showDeclineEventButtonHelper(eventDetail as iEventType | ICorporateEvent, eventType);
	}, [eventDetail, eventType]);

	const vendorSendBirdId = useMemo(() => {
		if (eventType === EventTypeFilterEnum.B2B) {
			return b2bEventDetail?.vendorSendBirdId ?? null;
		}
		return b2cEventDetail?.vendorSendBirdId ?? null;
	}, [eventType, b2bEventDetail?.vendorSendBirdId, b2cEventDetail?.vendorSendBirdId]);

	const rendertSendBirdComponent = () => {
		if (!chatMessage?.channelUrl) return null;

		return (
			<Box className="chatDetailContentWrapper">
				<Box className="chatDetailContent">
					<ChatPageSendbirdChat
						showLatestIcon={true}
						channelUrl={chatMessage.channelUrl}
						isNewModelChannel={chatMessage.isNewModelChannel === true}
						vendorSendBirdId={vendorSendBirdId}
						onChannelPreviewUpdate={handleChannelPreviewUpdate}
						onMessageUpdates={() => {
							setTimeout(() => {
								handleMessageUpdates();
							}, 2000);
						}}
					/>
				</Box>
			</Box>
		);
	};

	return (
		<Box className="chatDetailPage">
			{fetchingEventRecord ? (
				<ConversationSkeleton variant="event" />
			) : (
				<>
					{(!eventDetail || !eventType) && (
						<EmptyChatComponent
							onBackClick={isMobile ? handleBackClick : undefined}
							emptyChatHeading={emptyChatHeading}
						/>
					)}
					{eventDetail && eventType && (
						<>
							<Box className="chattingAndHeaderWrapper">
								{chatMessage && (
									<ChatDetailHeader
										eventDetail={eventDetail}
										eventType={eventType}
										onBackClick={
                      isMobile
                        ? () => {
                            fetchChatsRecord();
                            handleBackClick();
                          }
                        : undefined
                    }
										onEventStatusChange={handleEventStatusChange}
										onExclamationClick={handleDrawerOpen}
										handleStatusChange={handleStatusChange}
										chatMessage={chatMessage}
									/>
								)}
								{rendertSendBirdComponent()}
							</Box>
							{chatMessage && (
								<EventDetailsDrawer
									open={isDrawerOpen}
									onClose={handleDrawerClose}
									chatMessage={chatMessage}
									eventDetail={eventDetail}
									handleStatusChange={handleStatusChange}
									showDeclineEventButtonValue={showDeclineEventButtonValue}
								/>
							)}

							{eventType === EventTypeFilterEnum.Prepaid ? (
								<ShopifyDeclineModal
									modalHeaderText="Are you sure you want to Decline?"
									open={statusManagement.isDeclineModalOpen}
									onClose={() => {
										statusManagement.closeDeclineModal();
									}}
									shopifyDeclineModalBodyText="Are you sure you want to decline this event? This action can not be undone"
									declineOnClick={async () => {
										const eventWithRequest = eventDetail as iEventType;
										eventWithRequest?.request &&
											(await dispatch(declineEventRequestThunk(eventWithRequest.request.id)));
										dispatch(getEventDetailThunk({ id: eventDetail.id }));
										handleEventStatusChange();
										statusManagement.closeDeclineModal();
									}}
									backOnClick={() => {
										statusManagement.closeDeclineModal();
									}}
								/>
							) : (
								<DeclineEventModal
									modalHeaderText="Decline Event?"
									open={statusManagement.isDeclineModalOpen}
									onClose={statusManagement.closeDeclineModal}
									declineModalBodyTitle="Please specify a reason in order to decline the event"
									buttonText="Confirm"
									variant={BUTTON_VARIANT.CONTAINED}
									color={BUTTON_COLOR.ERROR}
									fullWidth={true}
									coreBtnPill={true}
									disabled={!statusManagement.radioGroupValue}
									onclick={handleDeclineEvent}
									RadioGroupValue={statusManagement.radioGroupValue}
									RadioGroupHandleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
										statusManagement.handleRadioGroupChange(event);
									}}
									radioLabels={DECLINE_VENDOR_REASONS}
									otherOnChange={(event: React.ChangeEvent<HTMLInputElement>) => {
										statusManagement.handleOtherChange(event);
									}}
									otherPlaceholder="Input a reason other than ones specified"
									loading={loading}
								/>
							)}
						</>
					)}
				</>
			)}
		</Box>
	);
};

export default ChatDetailPage;
